import { IonCol, IonLoading, IonPage, IonRow } from '@ionic/react';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import { CustomerMetadataSidebar } from '../components/metadata';
import { Conversation, UserContext } from '../contexts';
import { conversationResource } from '../resource';
import './conversationPage.scss';

const characterThemeMap: { [key: string]: string } = {
  blaen: 'bla',
  agaen: 'aga',
};

const characterNameMap: { [key: string]: string } = {
  blaen: 'blanche',
  agaen: 'agatha',
};

export const ConversationPage: React.FC = () => {
  const { idCustomer, idExpert } = useParams<{
    idCustomer: string;
    idExpert: string;
  }>();
  const { state } = useLocation() as {
    state: { idConversation?: string; readOnly?: boolean } | null;
  };
  const { isExpert } = useContext(UserContext);
  const [characterCode, setCharacterCode] = useState<string | null>(null);
  const [fetchedIdConversation, setFetchedIdConversation] = useState<
    string | null
  >(null);
  const [fetchedReadOnly, setFetchedReadOnly] = useState<boolean>(false);
  const [loadingFallback, setLoadingFallback] = useState(
    !state?.idConversation,
  );

  const idConversation = state?.idConversation || fetchedIdConversation;
  const readOnly = state?.idConversation ? !!state?.readOnly : fetchedReadOnly;

  useEffect(() => {
    if (
      !state?.idConversation &&
      !fetchedIdConversation &&
      idExpert &&
      idCustomer
    ) {
      conversationResource
        .findAll({
          'expert.id': idExpert,
          'customer.id': idCustomer,
          'order[updatedAt]': 'desc',
          perPage: 1,
          page: 1,
        })
        .then(response => {
          if (response.members.length > 0) {
            const conversation = response.members[0];
            setFetchedIdConversation(conversation['@id']);
            setFetchedReadOnly(conversation.status === 'finished');
          }
        })
        .catch(() => {
          // No conversation found
        })
        .finally(() => {
          setLoadingFallback(false);
        });
    }
  }, [state?.idConversation, fetchedIdConversation, idExpert, idCustomer]);

  useEffect(() => {
    if (idConversation) {
      conversationResource
        .find(idConversation)
        .then(response => {
          if (response && response.character) {
            setCharacterCode(response.character.shortCode);
          } else {
            setCharacterCode('blaen');
          }
        })
        .catch(() => {
          setCharacterCode('blaen');
        });
    }
  }, [idConversation]);

  if (loadingFallback) {
    return <IonLoading isOpen={true} message="Loading..." />;
  }

  if (!idConversation) {
    return null;
  }

  if (!characterCode) {
    return <IonLoading isOpen={true} message="Loading..." />;
  }

  const themePrefix = characterThemeMap[characterCode] || 'bla';
  const characterName = characterNameMap[characterCode] || 'blanche';
  const imageExtension = themePrefix === 'aga' ? 'webp' : 'png';

  return (
    <IonRow className={`expert-layout ${themePrefix}-theme`}>
      <IonCol sizeMd="3" className="ion-hide-sm-down">
        <img
          src={`/assets/img/${themePrefix}/i18n/en-US/left-${characterName}.${imageExtension}`}
          alt={''}
        />
      </IonCol>
      <IonCol sizeMd={isExpert ? '6' : '7'} className="chat-frame">
        {/* Remove IonPage element from Customer side to avoid ionic bug with ion-page-invisible */}
        {isExpert ? (
          <IonPage>
            <Conversation
              key={idConversation}
              idConversation={idConversation}
              idCustomer={idCustomer!}
              idExpert={idExpert!}
              readOnly={readOnly}
            />
          </IonPage>
        ) : (
          <div className="ion-page">
            <Conversation
              key={idConversation}
              idConversation={idConversation}
              idCustomer={idCustomer!}
              idExpert={idExpert!}
              readOnly={readOnly}
            />
          </div>
        )}
      </IonCol>
      {isExpert ? <IonCol sizeMd="1" className="ion-hide-sm-down" /> : null}
      {isExpert ? (
        <IonCol size="2" className="expert-sidebar">
          <CustomerMetadataSidebar
            idCustomer={idCustomer!}
            idConversation={idConversation}
            characterCode={characterCode}
          />
        </IonCol>
      ) : null}
    </IonRow>
  );
};
